import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  X, ChevronDown, ChevronUp, ExternalLink, ListMusic,
  SkipBack, SkipForward, Shuffle, Repeat, Repeat1, Heart, Trash2, Play,
} from "lucide-react";
import { FaSpotify, FaApple, FaYoutube } from "react-icons/fa";
import { useMusic } from "@/context/MusicPlayer";
import Artwork from "@/components/Artwork";

const TID_ROOT = "persistent-music-player";
const INACTIVITY_MS = 30_000;

const IconBtn = ({ children, active, onClick, ariaLabel, testid }) => (
  <button
    onClick={onClick}
    aria-label={ariaLabel}
    data-testid={testid}
    className={`p-2 rounded-full transition ${active ? "text-[#FF9933] bg-[#FF9933]/10" : "text-white/70 hover:text-[#FF9933] hover:bg-white/5"}`}
  >
    {children}
  </button>
);

const MusicDock = () => {
  const {
    current, queue, index, mode, setMode, open, close,
    next, prev, shuffle, setShuffle, repeat, setRepeat,
    favorites, toggleFavorite,
    queueDrawerOpen, setQueueDrawerOpen, removeFromQueue, jumpTo,
  } = useMusic();

  const [dragStartY, setDragStartY] = useState(null);
  const inactivityRef = useRef(null);

  // Auto-minimize after inactivity when in expanded mode
  useEffect(() => {
    if (!open || mode !== "expanded") return;
    const reset = () => {
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      inactivityRef.current = setTimeout(() => setMode("mini"), INACTIVITY_MS);
    };
    reset();
    const events = ["mousemove", "keydown", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset));
    return () => {
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [open, mode, setMode]);

  if (!open || !current) return null;

  const isFav = favorites.includes(current.id);

  const cycleRepeat = () => setRepeat(repeat === "off" ? "all" : repeat === "all" ? "one" : "off");

  const onTouchStart = (e) => setDragStartY(e.touches[0].clientY);
  const onTouchMove = (e) => {
    if (dragStartY == null) return;
    const dy = e.touches[0].clientY - dragStartY;
    if (dy > 60) {
      setMode(mode === "expanded" ? "mini" : "floating");
      setDragStartY(null);
    }
  };

  // ---------------- FLOATING (circular) mode ----------------
  if (mode === "floating") {
    return (
      <motion.button
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.6, opacity: 0 }}
        onClick={() => setMode("expanded")}
        data-testid={`${TID_ROOT}-floating`}
        className="fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full overflow-hidden shadow-[0_10px_40px_-8px_rgba(255,153,51,0.55)] ring-2 ring-[#FF9933]/60"
        aria-label={`Now playing ${current.title} — expand player`}
      >
        <Artwork src={current.cover_image} alt={current.title} className="w-full h-full object-cover" rounded="" />
        <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#FF9933] flex items-center justify-center border-2 border-black">
          <Play size={11} fill="black" className="text-black translate-x-0.5" />
        </span>
      </motion.button>
    );
  }

  // ---------------- MINI mode ----------------
  const miniMode = mode === "mini";

  return (
    <AnimatePresence>
      <motion.div
        key="dock"
        data-testid={TID_ROOT}
        initial={{ y: 120, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 120, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-4xl"
      >
        <div className="glass-strong rounded-2xl border border-[#FF9933]/25 shadow-[0_20px_60px_-20px_rgba(255,153,51,0.35)] overflow-hidden">
          {/* Track head bar */}
          <div className="flex items-center gap-3 px-3 md:px-4 py-3">
            <div className="w-11 h-11 flex-shrink-0">
              <Artwork src={current.cover_image} alt={current.title} className="w-full h-full object-cover" rounded="rounded-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-hindi text-sm text-[#FF9933] truncate">{current.hindi_title}</div>
              <Link to={`/releases/${current.id}`} className="text-sm text-white hover:text-[#FF9933] truncate block">
                {current.title}
              </Link>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 hidden sm:block">KASHiX{current.duration ? ` · ${current.duration}` : ""}</div>
            </div>

            {/* Compact transport (visible in both modes) */}
            <div className="flex items-center gap-1">
              <IconBtn onClick={prev} ariaLabel="Previous track" testid="player-prev"><SkipBack size={14} /></IconBtn>
              <IconBtn onClick={next} ariaLabel="Next track" testid="player-next"><SkipForward size={14} /></IconBtn>
            </div>

            <div className="hidden md:flex items-center gap-2 text-white/60">
              {current.spotify_url && (
                <a href={current.spotify_url} target="_blank" rel="noreferrer noopener" aria-label="Spotify" className="hover:text-[#FF9933]"><FaSpotify size={14} /></a>
              )}
              {current.apple_music_url && (
                <a href={current.apple_music_url} target="_blank" rel="noreferrer noopener" aria-label="Apple Music" className="hover:text-[#FF9933]"><FaApple size={14} /></a>
              )}
              {current.youtube_url && (
                <a href={current.youtube_url} target="_blank" rel="noreferrer noopener" aria-label="YouTube" className="hover:text-[#FF9933]"><FaYoutube size={14} /></a>
              )}
            </div>

            <IconBtn onClick={() => setQueueDrawerOpen(true)} ariaLabel="Open queue" testid="player-queue"><ListMusic size={14} /></IconBtn>
            <IconBtn
              onClick={() => setMode(miniMode ? "expanded" : "mini")}
              ariaLabel={miniMode ? "Expand player" : "Minimize player"}
              testid="music-player-minimize"
            >
              {miniMode ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </IconBtn>
            <IconBtn onClick={() => setMode("floating")} ariaLabel="Collapse to floating" testid="music-player-float">
              <span className="text-[10px] uppercase tracking-widest">Mini</span>
            </IconBtn>
            <IconBtn onClick={close} ariaLabel="Close player" testid="music-player-close"><X size={14} /></IconBtn>
          </div>

          {/* Expanded body */}
          <AnimatePresence initial={false}>
            {!miniMode && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="border-t border-white/5"
              >
                <iframe
                  key={current.id}
                  title={`Spotify player — ${current.title}`}
                  src={current.spotify_embed_url || "https://open.spotify.com/embed/artist/3CVKryef8hUWE97XMj2cw1?theme=0"}
                  width="100%"
                  height="152"
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  className="block bg-transparent"
                />
                <div className="flex items-center justify-between px-3 md:px-4 py-2.5 bg-black/40 border-t border-white/5">
                  <div className="flex items-center gap-1">
                    <IconBtn onClick={() => setShuffle(!shuffle)} active={shuffle} ariaLabel="Shuffle" testid="player-shuffle">
                      <Shuffle size={13} />
                    </IconBtn>
                    <IconBtn onClick={cycleRepeat} active={repeat !== "off"} ariaLabel={`Repeat ${repeat}`} testid="player-repeat">
                      {repeat === "one" ? <Repeat1 size={13} /> : <Repeat size={13} />}
                    </IconBtn>
                    <IconBtn onClick={() => toggleFavorite(current.id)} active={isFav} ariaLabel="Favorite" testid="player-fav">
                      <Heart size={13} fill={isFav ? "currentColor" : "none"} />
                    </IconBtn>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 hidden sm:block">
                    Track {index + 1} of {queue.length} · KASHiX
                  </div>
                  <a href={current.spotify_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-[#FF9933] hover:text-[#FFD700]">
                    Open in Spotify <ExternalLink size={11} />
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Queue drawer */}
        <AnimatePresence>
          {queueDrawerOpen && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              transition={{ duration: 0.35 }}
              className="mt-3 glass-strong rounded-2xl border border-white/10 overflow-hidden"
              data-testid="player-queue-drawer"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Queue · {queue.length}</div>
                <button
                  onClick={() => setQueueDrawerOpen(false)}
                  aria-label="Close queue"
                  className="text-white/60 hover:text-white text-[10px] uppercase tracking-widest"
                >
                  Close
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {queue.map((t, i) => (
                  <div
                    key={t.id}
                    className={`flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-none transition ${
                      i === index ? "bg-[#FF9933]/10" : "hover:bg-white/5"
                    }`}
                  >
                    <div className="w-8 text-[11px] text-white/40 text-right">{i + 1}</div>
                    <div className="w-10 h-10 flex-shrink-0">
                      <Artwork src={t.cover_image} alt="" className="w-full h-full object-cover" rounded="rounded" />
                    </div>
                    <button onClick={() => jumpTo(t.id)} className="flex-1 min-w-0 text-left">
                      <div className="text-sm text-white truncate">{t.title}</div>
                      <div className="text-[11px] text-white/50 truncate">{t.genre || "KASHiX"}</div>
                    </button>
                    {i === index && (
                      <div className="flex items-end gap-[2px] h-4">
                        {[0.1, 0.3, 0.2, 0.4].map((d, k) => (
                          <span key={k} className="w-[3px] bg-[#FF9933] eq-bar rounded-sm" style={{ height: "100%", animationDelay: `${d}s` }} />
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => removeFromQueue(t.id)}
                      aria-label="Remove from queue"
                      className="p-1.5 text-white/40 hover:text-red-400 transition"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default MusicDock;
